import { ImageUpload } from '@/components/ui/image-upload';
import { FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { FieldComponentProps } from '../fieldRegistry';
import { images_GenerateUploadURLRequest } from '@/types/image';
import type { AnyFieldConfig } from '../types';
import type { ImageUploadFieldConfig } from '@/types/formFields';

// The config type moved to @/types/formFields so FieldConfig can declare
// `uploadConfig` without importing this component module. Re-exported here so
// existing imports keep working.
export type { ImageUploadFieldConfig } from '@/types/formFields';

interface ImageUploadFieldProps extends Omit<FieldComponentProps<string>, 'field'> {
  field: AnyFieldConfig & {
    uploadConfig?: ImageUploadFieldConfig;
  };
}

export function ImageUploadField({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder,
}: ImageUploadFieldProps) {
  const uploadConfig = field.uploadConfig || {
    purpose: images_GenerateUploadURLRequest.purpose.ATTACHMENT,
    showPreview: true,
  };

  return (
    // `data-field` is the scroll/focus target owning forms use: this field
    // renders no `name` attribute anywhere in its markup.
    <FormItem data-field={field.name}>
      {field.label && <FormLabel htmlFor={id}>{field.label}</FormLabel>}
      <FormControl>
        <ImageUpload
          value={value}
          onChange={(imageId) => {
            onChange(imageId);
            onBlur();
          }}
          onRemove={() => {
            onChange('');
            onBlur();
          }}
          disabled={disabled || field.disabled}
          purpose={uploadConfig.purpose}
          entityType={uploadConfig.entityType}
          entityId={uploadConfig.entityId}
          description={uploadConfig.description || placeholder}
          showPreview={uploadConfig.showPreview}
          accept={uploadConfig.accept}
          maxSize={uploadConfig.maxSize}
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}